import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { Page, MaskedLines, FadeUp } from "@/components/Reveal";
import ProductCard from "@/components/ProductCard";
import QuickView from "@/components/QuickView";
import { PRODUCTS, CATEGORIES, LINES } from "@/data/products";

const FILTER_btn =
    "border px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.18em] transition-colors";

export default function Shop() {
    const [searchParams] = useSearchParams();
    const [cat, setCat] = useState("all");
    const [line, setLine] = useState("all");
    const [sort, setSort] = useState("featured");
    const [quick, setQuick] = useState(null);

    useEffect(() => {
        setCat(searchParams.get("cat") || "all");
        setLine(searchParams.get("line") || "all");
    }, [searchParams]);

    let list = PRODUCTS.filter(
        (p) => (cat === "all" || p.cat === cat) && (line === "all" || p.line === line),
    );
    if (sort === "price-asc") list = [...list].sort((a, b) => a.now - b.now);
    if (sort === "price-desc") list = [...list].sort((a, b) => b.now - a.now);
    if (sort === "name") list = [...list].sort((a, b) => a.name.localeCompare(b.name));

    const reset = () => {
        setCat("all");
        setLine("all");
        setSort("featured");
    };

    return (
        <Page className="pb-28">
            <section className="mx-auto w-full max-w-[1400px] px-5 pb-10 pt-36 md:px-8 md:pt-44 lg:px-10">
                <p className="flex items-center gap-3 text-[11px] font-mono uppercase tracking-[0.3em] text-gold">
                    <span className="inline-block h-px w-10 bg-gold" /> The Collection
                </p>
                <h1 className="mt-5 font-display text-5xl font-extrabold uppercase leading-[0.95] tracking-tight sm:text-6xl">
                    <MaskedLines lines={["The Collection."]} />
                </h1>
                <p className="mt-5 max-w-xl text-sm leading-relaxed text-mute md:text-base">
                    Limited-edition denim, cut in small batches and covered for life. Every piece
                    carries the URWAN stitch &amp; tear warranty.
                </p>
            </section>

            <section className="sticky top-16 z-30 border-y border-white/10 bg-obsidian/90 py-4 backdrop-blur-xl">
                <div className="mx-auto flex w-full max-w-[1400px] flex-col gap-4 px-5 md:px-8 lg:px-10">
                    <div className="flex flex-wrap items-center gap-2">
                        <span className="mr-2 hidden text-[10px] font-mono uppercase tracking-[0.25em] text-mute md:inline">
                            Category
                        </span>
                        {[{ id: "all", name: "All" }, ...CATEGORIES].map((c) => (
                            <button
                                key={c.id}
                                data-testid={`filter-cat-${c.id}`}
                                onClick={() => setCat(c.id)}
                                className={`${FILTER_btn} ${
                                    cat === c.id
                                        ? "border-gold bg-gold text-black"
                                        : "border-white/15 text-mute hover:border-gold/60 hover:text-ink"
                                }`}
                            >
                                {c.name}
                            </button>
                        ))}
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                        <span className="mr-2 hidden text-[10px] font-mono uppercase tracking-[0.25em] text-mute md:inline">
                            Line
                        </span>
                        <button
                            data-testid="filter-line-all"
                            onClick={() => setLine("all")}
                            className={`${FILTER_btn} ${
                                line === "all"
                                    ? "border-gold bg-gold text-black"
                                    : "border-white/15 text-mute hover:border-gold/60 hover:text-ink"
                            }`}
                        >
                            All Lines
                        </button>
                        {LINES.map((l) => (
                            <button
                                key={l.id}
                                data-testid={`filter-line-${l.id}`}
                                onClick={() => setLine(l.id)}
                                className={`${FILTER_btn} ${
                                    line === l.id
                                        ? "border-gold bg-gold text-black"
                                        : "border-white/15 text-mute hover:border-gold/60 hover:text-ink"
                                }`}
                            >
                                {l.name}
                            </button>
                        ))}
                    </div>
                </div>
            </section>

            <section className="mx-auto w-full max-w-[1400px] px-5 pt-8 md:px-8 lg:px-10">
                <div className="flex flex-wrap items-center justify-between gap-4">
                    <p className="text-xs font-mono uppercase tracking-[0.2em] text-mute" data-testid="shop-count">
                        Showing {list.length} of {PRODUCTS.length} results
                    </p>
                    <div className="relative">
                        <select
                            data-testid="sort-select"
                            value={sort}
                            onChange={(e) => setSort(e.target.value)}
                            className="appearance-none border border-white/15 bg-coal py-2.5 pl-4 pr-10 font-mono text-[11px] uppercase tracking-[0.15em] text-ink focus:border-gold focus:outline-none"
                        >
                            <option value="featured">Sort: Featured</option>
                            <option value="price-asc">Price: Low to High</option>
                            <option value="price-desc">Price: High to Low</option>
                            <option value="name">Name: A–Z</option>
                        </select>
                        <ChevronDown
                            size={14}
                            className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-mute"
                        />
                    </div>
                </div>

                <AnimatePresence mode="wait">
                    {list.length > 0 ? (
                        <motion.div
                            key={`${cat}-${line}-${sort}-${list.length}`}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -12 }}
                            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                            className="mt-10 grid grid-cols-2 gap-5 md:gap-8 lg:grid-cols-3 xl:grid-cols-4"
                        >
                            {list.map((p, i) => (
                                <ProductCard key={p.id} p={p} index={i} onQuickView={setQuick} />
                            ))}
                        </motion.div>
                    ) : (
                        <motion.div
                            key="empty"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="mt-20 border border-white/10 bg-coal/60 p-16 text-center"
                            data-testid="shop-empty-state"
                        >
                            <p className="font-display text-2xl font-bold text-ink">
                                Nothing here yet — this combination sold through.
                            </p>
                            <p className="mt-3 text-sm text-mute">
                                New pieces land often. Try another line, or clear the filters.
                            </p>
                            <button
                                onClick={reset}
                                data-testid="shop-reset-filters"
                                className="mt-7 border border-gold/60 px-7 py-3 font-display text-xs font-bold uppercase tracking-[0.2em] text-gold transition-colors hover:bg-gold hover:text-black"
                            >
                                Reset Filters
                            </button>
                        </motion.div>
                    )}
                </AnimatePresence>
            </section>

            <AnimatePresence>
                {quick && <QuickView p={quick} onClose={() => setQuick(null)} />}
            </AnimatePresence>
        </Page>
    );
}
